import { REFERRALS_ENABLED } from "./config/features";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useState, useEffect, Suspense, lazy } from "react";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LanguageProvider } from "./context/LanguageContext";
import { CompanyProvider, useCompany } from "./context/CompanyContext";
import { TrialProvider, useTrial } from "./context/TrialContext";
import { AppDataProvider } from "./context/AppDataContext";
import TrialBanner from "./components/TrialBanner";
import { AdminProvider, useAdmin } from "./context/AdminContext";

const Login = lazy(() => import("./pages/Login"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const CompanySettings = lazy(() => import("./pages/CompanySettings"));
const Clients = lazy(() => import("./pages/Clients"));
const Invoices = lazy(() => import("./pages/Invoices"));
const InvoiceForm = lazy(() => import("./pages/InvoiceForm"));
const Estimates = lazy(() => import("./pages/Estimates"));
const EstimateForm = lazy(() => import("./pages/EstimateForm"));
const Reports = lazy(() => import("./pages/Reports"));
const Refunds = lazy(() => import("./pages/Refunds"));
const Subscription = lazy(() => import("./pages/Subscription"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const SuspendedAccount = lazy(() => import("./pages/SuspendedAccount"));
const EstimateApproval = lazy(() => import("./pages/public/EstimateApproval"));
const InvoiceView = lazy(() => import("./pages/public/InvoiceView"));
const InvoiceGroupView = lazy(() => import("./pages/public/InvoiceGroupView"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const Jobs = lazy(() => import("./pages/Jobs"));
const Referral = lazy(() => import("./pages/Referral"));
const Join = lazy(() => import("./pages/Join"));
const Landing = lazy(() => import("./pages/Landing"));
const AdminPortal = lazy(() => import("./pages/AdminPortal"));
const LoggedOut = lazy(() => import("./pages/LoggedOut"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const FAQ = lazy(() => import("./pages/FAQ"));
const RefundPolicy = lazy(() => import("./pages/RefundPolicy"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));

function LoadingScreen() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
      <img src="/paylora-logo.png" alt="Paylora" className="h-20 w-auto mb-4 animate-pulse" />
    </div>
  );
}

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  const { trialInfo, loading: trialLoading } = useTrial();
  const { company, loading: companyLoading } = useCompany();
  const location = useLocation();

  if (loading || trialLoading || companyLoading) return (
    <LoadingScreen />
  );
  if (!user) return <Navigate to="/login" state={{ from: location }} />;
  if (trialInfo?.suspended) return <SuspendedAccount />;
  if (!user.emailVerified) return <VerifyEmail />;
  if (trialInfo && !trialInfo.hasAccess) return <Navigate to="/subscription" />;
  if (company && company.profileExists && !company.onboardingComplete) return <Navigate to="/onboarding" />;

  return (
    <>
      <TrialBanner />
      {children}
    </>
  );
}

// Shows landing for logged-out users, dashboard for logged-in users
function HomeRoute() {
  const { user, loading } = useAuth();
  const { company, loading: companyLoading } = useCompany();
  const { trialInfo, loading: trialLoading } = useTrial();
  const { isAdmin, loading: adminLoading } = useAdmin();
  const [timedOut, setTimedOut] = useState(false);
  const [graceExpired, setGraceExpired] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setTimedOut(true), 4000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setGraceExpired(true), 1200);
    return () => clearTimeout(t);
  }, []);

  const stillLoading = loading || companyLoading || trialLoading || adminLoading;
  const isPublicRoute = window.location.pathname.startsWith("/invoice/") || window.location.pathname.startsWith("/estimate/") || window.location.pathname.startsWith("/invoice-group/");

  if (stillLoading && !timedOut && !isPublicRoute) return (
    <LoadingScreen />
  );

  if (!user) {
    // If we were logged in a moment ago, this is very likely just the
    // brief window between navigating here and Firebase's auth listener
    // catching up - show a loading screen instead of flashing the
    // marketing page, and only fall through to Landing once that grace
    // period genuinely expires without a user showing up.
    const wasLoggedIn = sessionStorage.getItem("wasLoggedIn") === "1";
    if (wasLoggedIn && !graceExpired) {
      return <LoadingScreen />;
    }
    sessionStorage.removeItem("wasLoggedIn");
    return <Landing />;
  }
  sessionStorage.setItem("wasLoggedIn", "1");
  if (isAdmin) return <Navigate to="/admin" />;
  if (trialInfo?.suspended) return <SuspendedAccount />;
  if (!user.emailVerified) return <VerifyEmail />;
  if (trialInfo && !trialInfo.hasAccess) return <Navigate to="/subscription" />;
  if (company && company.profileExists && !company.onboardingComplete) return <Navigate to="/onboarding" />;
  return (
    <>
      <TrialBanner />
      <Dashboard />
    </>
  );
}

// Requires login but skips onboarding check
function AuthRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <LoadingScreen />
  );
  if (!user) return <Navigate to="/login" />;
  return children;
}

function AppRoutes() {
  const location = useLocation();
  return (
    <Suspense fallback={<LoadingScreen />}>
    <Routes>
              <Route path="/estimate/:userId/:estimateId" element={<EstimateApproval />} />
              <Route path="/invoice/:userId/:invoiceId" element={<InvoiceView />} />
              <Route path="/invoice-group/:userId/:groupId" element={<InvoiceGroupView />} />
              <Route path="/login" element={<Login />} />
              <Route path="/landing" element={<Landing />} />
              <Route path="/logged-out" element={<LoggedOut />} />
              <Route path="/admin" element={<AdminPortal />} />
              <Route path="/" element={<HomeRoute />} />
              <Route path="/onboarding" element={<Onboarding />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/refund-policy" element={<RefundPolicy />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
              <Route path="/settings" element={<PrivateRoute><CompanySettings /></PrivateRoute>} />
              <Route path="/clients" element={<PrivateRoute><Clients /></PrivateRoute>} />
              <Route path="/invoices" element={<PrivateRoute><Invoices /></PrivateRoute>} />
              <Route path="/invoices/new" element={<PrivateRoute><InvoiceForm key={location.key} /></PrivateRoute>} />
              <Route path="/invoices/:invoiceId/edit" element={<PrivateRoute><InvoiceForm key={location.key} /></PrivateRoute>} />
              <Route path="/estimates" element={<PrivateRoute><Estimates /></PrivateRoute>} />
              <Route path="/estimates/new" element={<PrivateRoute><EstimateForm key={location.key} /></PrivateRoute>} />
              <Route path="/estimates/:estimateId/edit" element={<PrivateRoute><EstimateForm key={location.key} /></PrivateRoute>} />
              <Route path="/jobs" element={<PrivateRoute><Jobs /></PrivateRoute>} />
              <Route path="/referral" element={REFERRALS_ENABLED ? <PrivateRoute><Referral /></PrivateRoute> : <Navigate to="/" replace />} />
              <Route path="/join" element={REFERRALS_ENABLED ? <Join /> : <Navigate to="/" replace />} />
              <Route path="/reports" element={<PrivateRoute><Reports /></PrivateRoute>} />
              <Route path="/refunds" element={<PrivateRoute><Refunds /></PrivateRoute>} />
              <Route path="/subscription" element={<Subscription />} />
            </Routes>
            </Suspense>
  );
}

export default function App() {
  return (
    <LanguageProvider>
    <AuthProvider>
      <AppDataProvider>
      <CompanyProvider>
        <TrialProvider>
          <AdminProvider>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
          </AdminProvider>
        </TrialProvider>
      </CompanyProvider>
      </AppDataProvider>
    </AuthProvider>
    </LanguageProvider>
  );
}
